// src/pages/AdvisorClientsPage.js
//
// Ticket 073 (Advisor Intake I9). "My clients": the clients an advisor entered
// themselves. List, New client, Open (the intake for that client), Rename, Remove.
//
// These clients belong to this advisor alone: no consent, no handoff to the client, not
// in the firm pipeline (/advisor), not visible to colleagues. The list comes from
// intake-clients, which keys on the verified token. The chosen client travels to
// /advisor/intake in router state, never in a URL.
//
// Behind the same browser flag as /intake (App.js). All wording is in intakeConfig.js.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { CLIENT_NAME_MAX } from '../lib/sharedOptions'
import {
  ADVISOR_CLIENTS_TITLE, ADVISOR_CLIENTS_SUBTITLE, ADVISOR_CLIENTS_EMPTY, ADVISOR_CLIENTS_EMPTY_SUB,
  ADVISOR_NEW_CLIENT_LABEL, ADVISOR_NAME_LABEL, ADVISOR_CREATE_LABEL, ADVISOR_CREATING_LABEL, ADVISOR_CANCEL_LABEL,
  ADVISOR_OPEN_LABEL, ADVISOR_RENAME_LABEL, ADVISOR_SAVE_NAME_LABEL, ADVISOR_REMOVE_LABEL, ADVISOR_REMOVE_CONFIRM,
  ADVISOR_NO_YEARS, ADVISOR_CLIENTS_LOADING, ADVISOR_CLIENT_REMINDER, ADVISOR_CLIENT_ERRORS, shareDateText,
} from '../lib/intake/intakeConfig'
import { listClients, createClient, renameClient, removeClient } from '../lib/intake/intakeApi'
import { trackAdvisorClientCreated, trackAdvisorClientRemoved } from '../lib/intake/intakeAnalytics'

const errorText = (kind) => ADVISOR_CLIENT_ERRORS[kind] || ADVISOR_CLIENT_ERRORS.DEFAULT

// One id per "New client" form: a double click or a retry creates one client, not two.
// Not a secret (it only has to be unique), so the fallback for an old browser is a plain v4 shape.
function newRequestId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16)
    return (ch === 'x' ? r : (r % 4) + 8).toString(16)
  })
}

export default function AdvisorClientsPage() {
  const { session } = useAuth()
  const navigate = useNavigate()
  const token = session ? session.access_token : null

  const [clients, setClients] = useState(null)      // null while loading
  const [loadError, setLoadError] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [formError, setFormError] = useState(null)
  const requestId = useRef(null)
  const inFlight = useRef(false)
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [rowError, setRowError] = useState(null)    // { id, text }
  const [busyId, setBusyId] = useState(null)

  const refresh = useCallback(async () => {
    if (!token) return
    const result = await listClients({ accessToken: token })
    if (result.kind === 'OK' && result.body && Array.isArray(result.body.clients)) {
      setClients(result.body.clients)
      setLoadError(null)
    } else {
      setLoadError(errorText(result.kind))
    }
  }, [token])

  useEffect(() => { refresh() }, [refresh])

  function openForm() {
    requestId.current = newRequestId()
    setName('')
    setFormError(null)
    setFormOpen(true)
  }

  async function handleCreate(e) {
    e.preventDefault()
    const trimmed = name.trim()
    if (inFlight.current || !token) return
    if (trimmed.length < 1 || trimmed.length > CLIENT_NAME_MAX || !requestId.current) {
      setFormError(errorText('INVALID_NAME'))
      return
    }
    inFlight.current = true
    setCreating(true)
    setFormError(null)
    const result = await createClient({ accessToken: token, clientRequestId: requestId.current, name: trimmed })
    inFlight.current = false
    setCreating(false)
    if (result.kind === 'OK') {
      if (!result.body.already_created) trackAdvisorClientCreated()
      setFormOpen(false)
      requestId.current = null
      await refresh()
      return
    }
    setFormError(errorText(result.kind))
  }

  function openClient(id) {
    navigate('/advisor/intake', { state: { subjectId: id } })
  }

  function startRename(c) {
    setEditingId(c.id)
    setEditName(c.name)
    setRowError(null)
  }

  async function saveRename(c) {
    const trimmed = editName.trim()
    if (busyId || !token) return
    if (trimmed.length < 1 || trimmed.length > CLIENT_NAME_MAX) {
      setRowError({ id: c.id, text: errorText('INVALID_NAME') })
      return
    }
    setBusyId(c.id)
    const result = await renameClient({ accessToken: token, subjectId: c.id, name: trimmed })
    setBusyId(null)
    if (result.kind === 'OK') {
      setEditingId(null)
      setRowError(null)
      await refresh()
      return
    }
    setRowError({ id: c.id, text: errorText(result.kind) })
  }

  async function handleRemove(c) {
    if (busyId || !token) return
    if (!window.confirm(ADVISOR_REMOVE_CONFIRM(c.name))) return
    setBusyId(c.id)
    setRowError(null)
    const result = await removeClient({ accessToken: token, subjectId: c.id })
    setBusyId(null)
    // NO_CLIENT means it is already gone: the refresh shows that.
    if (result.kind === 'OK' || result.kind === 'NO_CLIENT') {
      if (result.kind === 'OK') trackAdvisorClientRemoved()
      await refresh()
      return
    }
    setRowError({ id: c.id, text: errorText(result.kind) })
  }

  return (
    <div className="advisor-page">
      <div className="advisor-page-header advisor-clients-header">
        <div>
          <h1 className="advisor-page-title">{ADVISOR_CLIENTS_TITLE}</h1>
          <p className="u-subtext">{ADVISOR_CLIENTS_SUBTITLE}</p>
        </div>
        {!formOpen && (
          <button type="button" className="btn-primary" onClick={openForm}>{ADVISOR_NEW_CLIENT_LABEL}</button>
        )}
      </div>

      {formOpen && (
        <form className="advisor-clients-form" onSubmit={handleCreate}>
          <label className="u-form-label" htmlFor="advisor-client-name">{ADVISOR_NAME_LABEL}</label>
          <input
            id="advisor-client-name"
            className="app-input"
            type="text"
            value={name}
            maxLength={CLIENT_NAME_MAX}
            autoComplete="off"
            onChange={(e) => setName(e.target.value)}
          />
          <p className="advisor-clients-reminder">{ADVISOR_CLIENT_REMINDER}</p>
          {formError && <p className="advisor-error" role="alert">{formError}</p>}
          <div className="advisor-clients-actions">
            <button type="submit" className="btn-primary" disabled={creating || name.trim().length === 0}>
              {creating ? ADVISOR_CREATING_LABEL : ADVISOR_CREATE_LABEL}
            </button>
            <button type="button" className="btn-secondary" disabled={creating} onClick={() => setFormOpen(false)}>
              {ADVISOR_CANCEL_LABEL}
            </button>
          </div>
        </form>
      )}

      {loadError && <p className="advisor-error" role="alert">{loadError}</p>}

      <div className="advisor-table-wrap">
        {clients === null && !loadError && <div className="advisor-loading">{ADVISOR_CLIENTS_LOADING}</div>}
        {clients !== null && clients.length === 0 && (
          <div className="advisor-empty">
            <div className="advisor-empty-text">{ADVISOR_CLIENTS_EMPTY}</div>
            <div className="advisor-empty-sub">{ADVISOR_CLIENTS_EMPTY_SUB}</div>
          </div>
        )}
        {clients !== null && clients.length > 0 && (
          <ul className="advisor-clients-list">
            {clients.map((c) => {
              const editing = editingId === c.id
              const years = Array.isArray(c.years) && c.years.length > 0 ? c.years.join(', ') : ADVISOR_NO_YEARS
              const created = c.created_at ? shareDateText(c.created_at) : ''
              const last = c.last_saved_at ? shareDateText(c.last_saved_at) : ''
              return (
                <li key={c.id} className="advisor-clients-item">
                  <div className="advisor-clients-main">
                    {editing ? (
                      <input
                        className="app-input"
                        type="text"
                        aria-label={ADVISOR_NAME_LABEL}
                        value={editName}
                        maxLength={CLIENT_NAME_MAX}
                        autoComplete="off"
                        onChange={(e) => setEditName(e.target.value)}
                      />
                    ) : (
                      <div className="u-label-base">{c.name}</div>
                    )}
                    <div className="u-name-sub">
                      {[created && `Created ${created}`, `Tax years: ${years}`, last && `Last saved ${last}`].filter(Boolean).join(' · ')}
                    </div>
                    {rowError && rowError.id === c.id && <p className="advisor-error" role="alert">{rowError.text}</p>}
                  </div>
                  <div className="advisor-clients-actions">
                    {editing ? (
                      <>
                        <button type="button" className="btn-primary" disabled={busyId === c.id} onClick={() => saveRename(c)}>{ADVISOR_SAVE_NAME_LABEL}</button>
                        <button type="button" className="btn-secondary" disabled={busyId === c.id} onClick={() => setEditingId(null)}>{ADVISOR_CANCEL_LABEL}</button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="btn-primary" onClick={() => openClient(c.id)}>{ADVISOR_OPEN_LABEL}</button>
                        <button type="button" className="btn-secondary" disabled={busyId === c.id} onClick={() => startRename(c)}>{ADVISOR_RENAME_LABEL}</button>
                        <button type="button" className="btn-secondary" disabled={busyId === c.id} onClick={() => handleRemove(c)}>{ADVISOR_REMOVE_LABEL}</button>
                      </>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
